import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import IconButton from "components/shared/IconButton";
import useFormDisableFields from "../../hooks/useFormDisableFields";
import useFormText from "../../hooks/useFormText";
import {
  AI_TOOL_NAMES,
  AMPLITUDE_QUESTION_ASKED_LARGE_ATTRIBUTES,
  AMPLITUDE_QUESTION_ASKED_SMALL_ATTRIBUTES,
  AMPLITUDE_QUESTION_ASKED_SMALL_COLLAPSED_ATTRIBUTES,
  AMPLITUDE_SIGNUP_FORM_LARGE_ATTRIBUTES,
  AMPLITUDE_SIGNUP_FORM_SMALL_COLLAPSED_ATTRIBUTES
} from "../../constants";
import AIFormTitle from "./AIFormTitle";
import LockedInformationBanner from "./LockedInformationBanner";
import AIFormSubheading from "./AIFormSubheading";
import DisclaimerText from "./DisclaimerText";

const AIFormLayout = (props) => {
  const {
    children,
    collapsed,
    formik,
    infiniteAnswers,
    isLessThanSmallBreakpoint,
    loading,
    locked,
    quotaRemaining,
    resetDate,
    setCollapsed,
    signupPath,
    toolName
  } = props;

  const { disableFields } = useFormDisableFields(
    locked,
    quotaRemaining,
    infiniteAnswers
  );
  const { buttonText, resetText, submittingButtonText } = useFormText(
    toolName,
    locked,
    quotaRemaining,
    collapsed,
    isLessThanSmallBreakpoint,
    resetDate,
    infiniteAnswers,
    false // inContent is false for AIFormLayout
  );
  const showDisclaimerText = !collapsed || (collapsed && quotaRemaining === 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Locked, redirect to sales page
    if (locked && !disableFields) {
      window.location.href = signupPath;
      return;
    }

    // We're in a collapsed state (smaller breakpoints)
    // Expand the form
    if (collapsed) {
      setCollapsed(false);
      return;
    }

    formik.handleSubmit();
  };

  const amplitudeSignupAttr = () => {
    if (locked && !disableFields) {
      if (isLessThanSmallBreakpoint) {
        return collapsed
          ? AMPLITUDE_SIGNUP_FORM_SMALL_COLLAPSED_ATTRIBUTES[toolName]
          : AMPLITUDE_SIGNUP_FORM_LARGE_ATTRIBUTES[toolName];
      }
      return AMPLITUDE_SIGNUP_FORM_LARGE_ATTRIBUTES[toolName];
    }
    if (isLessThanSmallBreakpoint) {
      return collapsed
        ? AMPLITUDE_QUESTION_ASKED_SMALL_COLLAPSED_ATTRIBUTES[toolName]
        : AMPLITUDE_QUESTION_ASKED_SMALL_ATTRIBUTES[toolName];
    }
    return AMPLITUDE_QUESTION_ASKED_LARGE_ATTRIBUTES[toolName];
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={classnames("ai-form--container", { collapsed })}
    >
      <div className="ai-form--header">
        <AIFormTitle toolName={toolName} />
        <AIFormSubheading toolName={toolName} />
        <IconButton
          iconClass="lc-icon-close lc-icon-xs"
          btnClass="btn-lg btn-borderless-alt"
          onClick={() => setCollapsed(true)}
        />
      </div>
      <div className="ai-form--content">
        <div className="ai-form--body">
          {!infiniteAnswers && (
            <div
              className="ai-form--banner lc-banner lc-banner-information"
              data-testid="ai-form-banner"
            >
              {locked ? (
                <LockedInformationBanner
                  toolName={toolName}
                  signupPath={signupPath}
                />
              ) : (
                resetText
              )}
            </div>
          )}
          <div className="ai-form--fields">{children}</div>
        </div>
        <div className="ai-form--footer">
          <button
            className={`ai-form--submit btn btn-a-plus button-1 ${
              isLessThanSmallBreakpoint ? "btn-lg" : "btn-block"
            }`}
            data-testid="ai-form-submit"
            data-amplitude={amplitudeSignupAttr()}
            disabled={disableFields || loading}
            type="submit"
          >
            {locked && (
              <i
                className="lc-icon lc-icon-lock-filled lc-icon-sm"
                data-testid="lock-icon"
              />
            )}
            {loading ? submittingButtonText : buttonText}
          </button>
          {!locked && showDisclaimerText && (
            <div className="subtitle-1 tw-flex tw-pt-2 tw-text-gray-tint-40">
              {collapsed && quotaRemaining === 0 && resetText}
              {!collapsed && (
                <DisclaimerText className="tw-self-center tw-flex-grow" />
              )}
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

AIFormLayout.propTypes = {
  children: PropTypes.func.isRequired,
  collapsed: PropTypes.bool.isRequired,
  formik: PropTypes.shape({
    errors: PropTypes.object,
    handleBlur: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    touched: PropTypes.object,
    values: PropTypes.object.isRequired
  }).isRequired,
  infiniteAnswers: PropTypes.bool,
  isLessThanSmallBreakpoint: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  locked: PropTypes.bool,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  setCollapsed: PropTypes.func.isRequired,
  signupPath: PropTypes.string,
  toolName: PropTypes.oneOf(Object.values(AI_TOOL_NAMES)).isRequired
};

AIFormLayout.defaultProps = {
  loading: false,
  locked: false,
  resetDate: "",
  signupPath: "/sign-up?focus=teacher"
};

export default AIFormLayout;
